import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Breadcrumb } from "@/components/feature/PageHero";
import TitleCard from "@/components/feature/TitleCard";
import NewsCard from "@/components/feature/NewsCard";
import { useToast } from "@/components/base/Toast";
import { useSeo } from "@/hooks/useSeo";
import { getRelatedTitles, getTitleById, titles } from "@/mocks/titles";
import { getNewsByTitleId } from "@/mocks/news";
import CastList from "@/pages/detail/components/CastList";
import DetailActions from "@/pages/detail/components/DetailActions";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const { notify } = useToast();
  const title = getTitleById(id);
  const [saved, setSaved] = useState(false);

  useSeo({
    title: title ? `${title.name} - 星光传媒影视详情` : "影视详情 - 星光传媒",
    description: title
      ? `${title.name}（${title.year}，${title.region}）${title.type === "movie" ? "电影" : "剧集"}，导演 ${title.director}，主演 ${title.cast
          .slice(0, 3)
          .map((member) => member.name)
          .join("、")}。${title.tagline} 星光传媒影视详情页提供剧情简介、演职人员与相关推荐。`
      : "星光传媒影视详情页，提供作品介绍、演职人员与相关推荐。",
    keywords: title ? `${title.name},${title.genres.join(",")},${title.director},星光传媒,影视详情` : "星光传媒,影视详情",
    canonicalPath: `/detail/${id ?? ""}`,
  });

  useEffect(() => {
    setSaved(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  const related = useMemo(() => (title ? getRelatedTitles(title, 4) : []), [title]);
  const relatedNews = useMemo(() => (id ? getNewsByTitleId(id) : []), [id]);
  const ranking = useMemo(
    () => titles.filter((item) => item.type === title?.type && item.id !== title?.id).sort((a, b) => b.hot - a.hot).slice(0, 5),
    [title],
  );

  if (!title) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar currentLabel="首页" />
        <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-400 items-center justify-center mb-5">
              <i className="ri-film-line text-3xl w-8 h-8 flex items-center justify-center"></i>
            </span>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">没有找到这部作品</h1>
            <p className="text-sm text-foreground-500 mb-6">链接可能已失效，或者该作品尚未收录。你可以回到分类页继续浏览。</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/category"
                className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
              >
                返回影视分类
              </Link>
              <Link
                to="/"
                className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                回到首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLabel={title.type === "movie" ? "电影" : "电视剧"} />
      <main>
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={title.still}
              alt={`《${title.name}》剧照`}
              title={`${title.name} 星光传媒`}
              className="w-full h-full object-cover object-top opacity-40 blur-sm scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/85 via-primary-950/90 to-primary-950/95" />
          </div>
          <div className="relative z-10 px-4 md:px-6 pt-24 md:pt-28 pb-10 md:pb-14">
            <div className="max-w-[1440px] mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: title.type === "movie" ? "电影推荐" : "电视剧热播", to: `/category?type=${title.type}` },
                  { label: title.name },
                ]}
              />
              <div className="mt-6 grid grid-cols-1 md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr] gap-6 md:gap-10">
                <div className="w-40 sm:w-48 md:w-full mx-auto md:mx-0">
                  <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-50/15">
                    <img
                      src={title.poster}
                      alt={`《${title.name}》海报`}
                      title={`${title.name} 星光传媒影视作品`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {[title.still, title.poster, title.still].map((src, index) => (
                      <div key={`${src}-${index}`} className="rounded overflow-hidden aspect-[4/3] bg-primary-900">
                        <img
                          src={src}
                          alt={`《${title.name}》剧照 ${index + 1}`}
                          title={`${title.name} 剧照`}
                          className="w-full h-full object-cover object-top opacity-80 hover:opacity-100 transition-opacity"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-background-50">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 rounded text-[11px] bg-accent-500 text-primary-950 font-medium">
                      {title.type === "movie" ? "电影" : "剧集"}
                    </span>
                    {title.genres.map((genre) => (
                      <Link
                        key={genre}
                        to={`/category?type=${title.type}`}
                        className="px-2.5 py-1 rounded text-[11px] bg-background-50/10 text-background-100 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {genre}
                      </Link>
                    ))}
                    <span className="text-[11px] text-background-300">{title.region}</span>
                  </div>
                  <h1 className="font-heading text-3xl md:text-5xl font-semibold leading-tight mb-1">{title.name}</h1>
                  <p className="text-xs md:text-sm tracking-[0.18em] uppercase text-background-300/70 mb-4">{title.enName}</p>
                  <p className="text-sm md:text-base text-background-200/90 leading-relaxed mb-5 max-w-2xl">{title.tagline}</p>

                  <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-heading text-3xl md:text-4xl font-semibold text-accent-400">{title.rating.toFixed(1)}</span>
                      <span className="text-xs text-background-300">/ 10 星光评分</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[0, 1, 2, 3, 4].map((star) => (
                        <i
                          key={star}
                          className={`${
                            star < Math.round(title.rating / 2) ? "ri-star-fill text-accent-400" : "ri-star-line text-background-300/40"
                          } text-sm w-4 h-4 flex items-center justify-center`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs text-background-300">人气 {title.hot}</span>
                  </div>

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm max-w-2xl mb-6">
                    {[
                      { label: "导演", value: title.director },
                      { label: "主演", value: title.cast.slice(0, 3).map((member) => member.name).join(" / ") },
                      { label: "类型", value: title.genres.join("、") },
                      { label: "地区", value: title.region },
                      { label: "年份", value: `${title.year}` },
                      { label: title.type === "movie" ? "片长" : "集数", value: title.duration },
                    ].map((row) => (
                      <div key={row.label} className="flex gap-3">
                        <dt className="text-background-400 shrink-0 w-10">{row.label}</dt>
                        <dd className="text-background-100">{row.value}</dd>
                      </div>
                    ))}
                  </dl>

                  <DetailActions
                    title={title}
                    saved={saved}
                    onToggleSave={() => {
                      setSaved((prev) => {
                        notify(prev ? "已取消收藏（前端演示，刷新后清空）" : "已加入收藏（前端演示，刷新后清空）");
                        return !prev;
                      });
                    }}
                    onShared={() => notify("分享链接已复制，可发送给朋友")}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 md:gap-10">
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">剧情简介</h2>
                </div>
                <p className="text-sm md:text-base text-foreground-700 leading-7 mb-4">{title.synopsis}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {title.highlights.map((item, index) => (
                    <div key={item} className="bg-background-100 rounded-lg p-4">
                      <span className="font-heading text-sm text-accent-700">0{index + 1}</span>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mt-1.5">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">演员列表</h2>
                  </div>
                  <span className="text-xs text-foreground-400">虚构示例</span>
                </div>
                <CastList cast={title.cast} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">相关推荐</h2>
                  </div>
                  <Link
                    to={`/category?type=${title.type}`}
                    className="text-xs text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    更多同类
                    <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
                  {related.map((item) => (
                    <TitleCard key={item.id} title={item} />
                  ))}
                </div>
              </div>
            </div>

            <aside className="space-y-6">
              <div className="bg-background-100 rounded-lg p-4 md:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-medium text-foreground-950 flex items-center gap-1.5">
                    <i className="ri-newspaper-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                    关联资讯
                  </h2>
                  <Link to="/news" className="text-[11px] text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap">
                    全部
                  </Link>
                </div>
                {relatedNews.length > 0 ? (
                  <div>
                    {relatedNews.map((item) => (
                      <NewsCard key={item.id} item={item} variant="horizontal" />
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center bg-background-50 rounded-md">
                    <p className="text-xs text-foreground-500">暂无该作品的关联资讯</p>
                    <Link to="/news" className="text-xs text-primary-700 mt-2 inline-block cursor-pointer">
                      去看看影视资讯
                    </Link>
                  </div>
                )}
              </div>

              <div className="bg-primary-950 rounded-lg p-4 md:p-5 text-background-200">
                <h2 className="text-sm font-medium text-background-50 mb-3 flex items-center gap-1.5">
                  <i className="ri-fire-line text-accent-400 w-4 h-4 flex items-center justify-center"></i>
                  {title.type === "movie" ? "热门电影榜" : "热播剧集榜"}
                </h2>
                <ol className="space-y-2.5">
                  {ranking.map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/detail/${item.id}`} className="flex items-center gap-2.5 group cursor-pointer">
                        <span
                          className={`font-heading text-sm w-4 shrink-0 ${
                            index < 3 ? "text-accent-400" : "text-background-400"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1 text-xs text-background-200 group-hover:text-background-50 truncate">
                          {item.name}
                        </span>
                        <span className="text-[11px] text-background-400 tabular-nums">{item.rating.toFixed(1)}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg p-4 md:p-5 bg-accent-50 border border-accent-200">
                <p className="text-xs text-accent-900 leading-relaxed">
                  <i className="ri-information-line mr-1 w-3.5 h-3.5 inline-flex items-center justify-center"></i>
                  本页作品、演职人员与评分为虚构演示数据，仅用于展示网站内容结构，不构成真实观影建议。
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}